'use client';
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
import * as React from 'react';
import PropTypes from 'prop-types';
import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react-dom';
import { HTMLElementType, unstable_useEnhancedEffect as useEnhancedEffect, unstable_useForkRef as useForkRef } from '@mui/utils';
import { unstable_composeClasses as composeClasses } from "../composeClasses/index.js";
import { Portal } from "../Portal/index.js";
import { useSlotProps } from "../utils/index.js";
import { useClassNamesOverride } from "../utils/ClassNameConfigurator.js";
import { getPopupUtilityClass } from "./popupClasses.js";
import { useTransitionTrigger, TransitionContext } from "../useTransition/index.js";
import { PopupContext } from "./PopupContext.js";
import { jsx as _jsx } from "react/jsx-runtime";
function useUtilityClasses(ownerState) {
    var open = ownerState.open;
    var slots = {
        root: [
            'root',
            open && 'open'
        ]
    };
    return composeClasses(slots, useClassNamesOverride(getPopupUtilityClass));
}
function resolveAnchor(anchor) {
    return typeof anchor === 'function' ? anchor() : anchor;
}
/**
 *
 * Demos:
 *
 * - [Popup](https://mui.com/base-ui/react-popup/)
 *
 * API:
 *
 * - [Popup API](https://mui.com/base-ui/react-popup/components-api/#popup)
 */ var Popup = /*#__PURE__*/ React.forwardRef(function Popup(props, forwardedRef) {
    var anchorProp = props.anchor, children = props.children, container = props.container, _props_disablePortal = props.disablePortal, disablePortal = _props_disablePortal === void 0 ? false : _props_disablePortal, _props_keepMounted = props.keepMounted, keepMounted = _props_keepMounted === void 0 ? false : _props_keepMounted, middleware = props.middleware, tmp = props.offset, offsetProp = tmp === void 0 ? 0 : tmp, _props_open = props.open, open = _props_open === void 0 ? false : _props_open, _props_placement = props.placement, placement = _props_placement === void 0 ? 'bottom' : _props_placement, _props_slotProps = props.slotProps, slotProps = _props_slotProps === void 0 ? {} : _props_slotProps, _props_slots = props.slots, slots = _props_slots === void 0 ? {} : _props_slots, _props_strategy = props.strategy, strategy = _props_strategy === void 0 ? 'absolute' : _props_strategy, other = _object_without_properties(props, [
        "anchor",
        "children",
        "container",
        "disablePortal",
        "keepMounted",
        "middleware",
        "offset",
        "open",
        "placement",
        "slotProps",
        "slots",
        "strategy"
    ]);
    var _useFloating = useFloating({
        elements: {
            reference: resolveAnchor(anchorProp)
        },
        open: open,
        middleware: middleware !== null && middleware !== void 0 ? middleware : [
            offset(offsetProp !== null && offsetProp !== void 0 ? offsetProp : 0),
            flip(),
            shift()
        ],
        placement: placement,
        strategy: strategy,
        whileElementsMounted: !keepMounted ? autoUpdate : undefined
    }), refs = _useFloating.refs, elements = _useFloating.elements, floatingStyles = _useFloating.floatingStyles, update = _useFloating.update, finalPlacement = _useFloating.placement;
    var handleRef = useForkRef(refs.setFloating, forwardedRef);
    useEnhancedEffect(function() {
        if (keepMounted && open && elements.reference && elements.floating) {
            var cleanup = autoUpdate(elements.reference, elements.floating, update);
            return cleanup;
        }
        return undefined;
    }, [
        keepMounted,
        open,
        elements,
        update
    ]);
    var ownerState = _object_spread_props(_object_spread({}, props), {
        disablePortal: disablePortal,
        keepMounted: keepMounted,
        offset: offset,
        open: open,
        placement: placement,
        finalPlacement: finalPlacement,
        strategy: strategy
    });
    var _useTransitionTrigger = useTransitionTrigger(open), contextValue = _useTransitionTrigger.contextValue, hasTransitionExited = _useTransitionTrigger.hasExited;
    var visibility = keepMounted && hasTransitionExited ? 'hidden' : undefined;
    var classes = useUtilityClasses(ownerState);
    var _slots_root;
    var Root = (_slots_root = slots === null || slots === void 0 ? void 0 : slots.root) !== null && _slots_root !== void 0 ? _slots_root : 'div';
    var rootProps = useSlotProps({
        elementType: Root,
        externalSlotProps: slotProps.root,
        externalForwardedProps: other,
        ownerState: ownerState,
        className: classes.root,
        additionalProps: {
            ref: handleRef,
            role: 'tooltip',
            style: _object_spread_props(_object_spread({}, floatingStyles), {
                visibility: visibility
            })
        }
    });
    var popupContextValue = React.useMemo(function() {
        return {
            placement: finalPlacement
        };
    }, [
        finalPlacement
    ]);
    var shouldRender = keepMounted || !hasTransitionExited;
    if (!shouldRender) {
        return null;
    }
    return /*#__PURE__*/ _jsx(Portal, {
        disablePortal: disablePortal,
        container: container,
        children: /*#__PURE__*/ _jsx(PopupContext.Provider, {
            value: popupContextValue,
            children: /*#__PURE__*/ _jsx(TransitionContext.Provider, {
                value: contextValue,
                children: /*#__PURE__*/ _jsx(Root, _object_spread_props(_object_spread({}, rootProps), {
                    children: children
                }))
            })
        })
    });
});
process.env.NODE_ENV !== "production" ? Popup.propTypes = {
    // ┌────────────────────────────── Warning ──────────────────────────────┐
    // │ These PropTypes are generated from the TypeScript type definitions. │
    // │ To update them, edit the TypeScript types and run `pnpm proptypes`. │
    // └─────────────────────────────────────────────────────────────────────┘
    /**
   * An HTML element, [virtual element](https://floating-ui.com/docs/virtual-elements),
   * or a function that returns either.
   * It's used to set the position of the popup.
   */ anchor: PropTypes /* @typescript-to-proptypes-ignore */ .oneOfType([
        HTMLElementType,
        PropTypes.object,
        PropTypes.func
    ]),
    /**
   * @ignore
   */ children: PropTypes /* @typescript-to-proptypes-ignore */ .oneOfType([
        PropTypes.node,
        PropTypes.func
    ]),
    /**
   * An HTML element or function that returns one. The container will have the portal children appended to it.
   * By default, it uses the body of the top-level document object, so it's `document.body` in these cases.
   */ container: PropTypes /* @typescript-to-proptypes-ignore */ .oneOfType([
        HTMLElementType,
        PropTypes.func
    ]),
    /**
   * If `true`, the popup will be rendered where it is defined, without the use of portals.
   * @default false
   */ disablePortal: PropTypes.bool,
    /**
   * If `true`, the popup will exist in the DOM even if it's closed.
   * Its visibility will be controlled by the `visibility` CSS property.
   *
   * Otherwise, a closed popup will be removed from the DOM.
   *
   * @default false
   */ keepMounted: PropTypes.bool,
    /**
   * Collection of Floating UI middleware to use when positioning the popup.
   * If not provided, the [`offset`](https://floating-ui.com/docs/offset)
   * and [`flip`](https://floating-ui.com/docs/flip) functions will be used.
   *
   * @see https://floating-ui.com/docs/computePosition#middleware
   */ middleware: PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.oneOf([
            false
        ]),
        PropTypes.shape({
            fn: PropTypes.func.isRequired,
            name: PropTypes.string.isRequired,
            options: PropTypes.any
        })
    ])),
    /**
   * Distance between a popup and the trigger element.
   * This prop is ignored when custom `middleware` is provided.
   *
   * @default 0
   * @see https://floating-ui.com/docs/offset
   */ offset: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.number,
        PropTypes.shape({
            alignmentAxis: PropTypes.number,
            crossAxis: PropTypes.number,
            mainAxis: PropTypes.number
        })
    ]),
    /**
   * If `true`, the popup is visible.
   *
   * @default false
   */ open: PropTypes.bool,
    /**
   * Determines where to place the popup relative to the trigger element.
   *
   * @default 'bottom'
   * @see https://floating-ui.com/docs/computePosition#placement
   */ placement: PropTypes.oneOf([
        'bottom-end',
        'bottom-start',
        'bottom',
        'left-end',
        'left-start',
        'left',
        'right-end',
        'right-start',
        'right',
        'top-end',
        'top-start',
        'top'
    ]),
    /**
   * The props used for each slot inside the Popup.
   *
   * @default {}
   */ slotProps: PropTypes.shape({
        root: PropTypes.oneOfType([
            PropTypes.func,
            PropTypes.object
        ])
    }),
    /**
   * The components used for each slot inside the Popup.
   * Either a string to use a HTML element or a component.
   *
   * @default {}
   */ slots: PropTypes.shape({
        root: PropTypes.elementType
    }),
    /**
   * The type of CSS position property to use (absolute or fixed).
   *
   * @default 'absolute'
   * @see https://floating-ui.com/docs/computePosition#strategy
   */ strategy: PropTypes.oneOf([
        'absolute',
        'fixed'
    ])
} : void 0;
export { Popup };
